import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faUsers, faMoneyBillWave, faNetworkWired, faLink, 
    faCopy, faCheckCircle, faChartLine, faArrowUpRightFromSquare,
    faGem, faHistory
} from '@fortawesome/free-solid-svg-icons';

const api = axios.create({ baseURL: 'https://api.blackroq.pro/api' });

const LeaderCommissions = () => {
    const [tunnels, setTunnels] = useState([]);
    const [commissions, setCommissions] = useState([]);
    const [stats, setStats] = useState({ totalEarned: 0, totalCommissions: 0 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [copiedId, setCopiedId] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchData = async () => {
            try {
                const token = localStorage.getItem('token');
                if (!token) { navigate('/login'); return; }
                const config = { headers: { Authorization: `Bearer ${token}` } };

                const [tunnelsRes, commissionsRes] = await Promise.all([
                    api.get('/user/tunnels', config),
                    api.get('/user/commissions', config)
                ]);

                setTunnels(tunnelsRes.data);
                setCommissions(commissionsRes.data);
                const totalEarned = commissionsRes.data.reduce((sum, c) => sum + c.commissionAmount, 0);
                setStats({ totalEarned, totalCommissions: commissionsRes.data.length });
                setLoading(false);
            } catch (err) {
                setError(err.response?.data?.message || 'Erreur de chargement.');
                setLoading(false);
            }
        };
        fetchData();
    }, [navigate]);

    const handleCopy = (link, id) => {
        navigator.clipboard.writeText(link);
        setCopiedId(id);
        setTimeout(() => setCopiedId(null), 2000);
    };

    const getCurrentRate = (count) => {
        if (count < 50) return 10;
        if (count < 100) return 7;
        if (count < 150) return 4;
        return 1;
    };

    if (loading) return (
        <div className="d-flex justify-content-center align-items-center" style={{height: '90vh'}}>
            <div className="spinner-border text-warning" style={{width: '3rem', height: '3rem'}} role="status"></div>
        </div>
    );

    return (
        <div className="container py-4" style={{ maxWidth: '1100px' }}>
            {/* --- HEADER PREMIUM --- */}
            <div className="row mb-5 align-items-center bg-white p-4 rounded-4 shadow-sm mx-0 border-bottom border-warning border-4">
                <div className="col-auto">
                    <div className="bg-dark text-warning rounded-4 p-3 shadow">
                        <FontAwesomeIcon icon={faGem} size="2x" />
                    </div>
                </div>
                <div className="col">
                    <h2 className="fw-black mb-1 text-dark">Tableau de Bord Leader</h2>
                    <p className="text-muted mb-0 font-monospace small">ID : {localStorage.getItem('userId')?.substring(0,8) || 'ADMIN'} | Statut : Certifié Blackroq</p>
                </div>
                <div className="col-md-auto mt-3 mt-md-0">
                    <span className="badge bg-warning text-dark px-3 py-2 rounded-pill fw-bold">
                        Taux Actuel : {getCurrentRate(stats.totalCommissions)}%
                    </span>
                </div>
            </div>

            {/* --- STATS GRID --- */}
            <div className="row g-4 mb-5">
                <div className="col-md-4">
                    <div className="card border-0 rounded-4 shadow-sm bg-dark text-white overflow-hidden">
                        <div className="card-body p-4 position-relative">
                            <h6 className="text-uppercase text-warning small fw-bold mb-3">Revenus Totaux</h6>
                            <h2 className="display-6 fw-bold mb-0 text-white">
                                {stats.totalEarned.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                <span className="h4 ms-2 text-warning font-monospace">USDT</span>
                            </h2>
                            <FontAwesomeIcon icon={faMoneyBillWave} className="position-absolute opacity-10" style={{ right: '-10px', bottom: '-10px', fontSize: '5rem' }} />
                        </div>
                    </div>
                </div>

                <div className="col-md-8">
                    <div className="card border-0 rounded-4 shadow-sm h-100 bg-white">
                        <div className="card-body p-4">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h6 className="text-uppercase text-muted small fw-bold mb-0">Progression vers Prochain Palier</h6>
                                <span className="small fw-bold">{stats.totalCommissions} / 150</span>
                            </div>
                            <div className="progress rounded-pill shadow-sm" style={{ height: '20px', backgroundColor: '#f0f0f0' }}>
                                <div 
                                    className="progress-bar bg-warning progress-bar-striped progress-bar-animated" 
                                    style={{ width: `${Math.min((stats.totalCommissions / 150) * 100, 100)}%` }}
                                ></div>
                            </div>
                            <div className="d-flex justify-content-between mt-2 small text-muted">
                                <span>10% (0)</span>
                                <span>7% (50)</span>
                                <span>4% (100)</span>
                                <span>1% (150+)</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* --- SECTION TUNNELS --- */}
            <div className="mb-5">
                <div className="d-flex align-items-center mb-4">
                    <div className="bg-primary text-white rounded-3 p-2 me-3">
                        <FontAwesomeIcon icon={faLink} />
                    </div>
                    <h4 className="fw-bold mb-0">Mes Tunnels Actifs</h4>
                </div>

                <div className="row g-4">
                    {tunnels.map(tunnel => (
                        <div className="col-md-6" key={tunnel._id}>
                            <div className="card border-0 shadow-sm rounded-4 h-100 hover-shadow transition">
                                <div className="card-body p-4">
                                    <div className="d-flex justify-content-between mb-3">
                                        <span className="h5 fw-bold text-dark">{tunnel.amount.toLocaleString()} FCFA</span>
                                        <span className="badge bg-soft-success text-success border border-success px-3 py-2 rounded-pill">
                                            {tunnel.validationCount} Validations
                                        </span>
                                    </div>
                                    <div className="input-group shadow-sm rounded-3 overflow-hidden border">
                                        <span className="input-group-text bg-light border-0"><FontAwesomeIcon icon={faArrowUpRightFromSquare} className="small text-muted" /></span>
                                        <input 
                                            type="text" 
                                            className="form-control border-0 bg-light font-monospace small" 
                                            value={tunnel.referralLink} 
                                            readOnly 
                                        />
                                        <button 
                                            className={`btn btn-${copiedId === tunnel._id ? 'success' : 'dark'} px-4 border-0`}
                                            onClick={() => handleCopy(tunnel.referralLink, tunnel._id)}
                                        >
                                            <FontAwesomeIcon icon={copiedId === tunnel._id ? faCheckCircle : faCopy} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* --- SECTION HISTORIQUE --- */}
            <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
                <div className="card-header bg-white p-4 border-0 d-flex align-items-center">
                    <div className="bg-success text-white rounded-3 p-2 me-3">
                        <FontAwesomeIcon icon={faHistory} />
                    </div>
                    <h4 className="fw-bold mb-0">Flux des Commissions</h4>
                </div>
                <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                        <thead className="bg-light">
                            <tr className="text-uppercase small fw-bold text-muted">
                                <th className="px-4 py-3">Investisseur</th>
                                <th>Détails Transaction</th>
                                <th>Taux</th>
                                <th className="text-end px-4">Commission</th>
                            </tr>
                        </thead>
                        <tbody>
                            {commissions.map((c, index) => (
                                <tr key={c._id} className={index === 0 ? "bg-light-success" : ""}>
                                    <td className="px-4 py-3">
                                        <div className="d-flex align-items-center">
                                            <div className="avatar-sm bg-primary text-white rounded-circle me-3 d-flex align-items-center justify-content-center fw-bold">
                                                {c.investorId?.username?.charAt(0).toUpperCase()}
                                            </div>
                                            <div>
                                                <div className="fw-bold text-dark">{c.investorId?.username}</div>
                                                <div className="small text-muted font-monospace">{c.investorId?.email?.substring(0, 15)}...</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="small fw-bold">{c.investmentAmount.toLocaleString()} FCFA</div>
                                        <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                                            {format(new Date(c.createdAt), 'dd MMMM yyyy HH:mm', { locale: fr })}
                                        </div>
                                    </td>
                                    <td>
                                        <span className="badge rounded-pill bg-light text-dark border">
                                            {(c.commissionRate * 100).toFixed(0)}%
                                        </span>
                                    </td>
                                    <td className="text-end px-4">
                                        <div className="fw-black text-success h5 mb-0">
                                            +{c.commissionAmount.toFixed(2)} 
                                            <span className="small ms-1">USDT</span>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default LeaderCommissions;